import React, { useEffect, useMemo, useState } from "react";
import {
  BoltIcon,
  MapPinIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  DocumentArrowDownIcon,
  BellIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import {
  toastError,
  toastSuccess,
  toastLoading,
  toastDismiss,
  toastInfo,
} from "../utils/toast";
import conduttoreIcon from "../assets/icons/Service-Leader-Icon.png";
import plannerLogo from "../assets/logo/Logo-Planner-Turni.png";
import { registerMontserrat, FONT_MONTSERRAT } from "../utils/pdfFonts";

// Lazy loader per jsPDF/autotable, evita di caricare i moduli nel main bundle
let cachedJsPDF = null;
const loadJsPdf = async () => {
  if (cachedJsPDF) return cachedJsPDF;
  const [{ default: jsPDF }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);
  cachedJsPDF = jsPDF;
  return jsPDF;
};

const ConduttoreIcon = () => (
  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-600">
    <img
      src={conduttoreIcon}
      alt="Service leader"
      className="h-10 w-10 object-contain"
    />
  </span>
);

const monthNames = [
  "Gennaio",
  "Febbraio",
  "Marzo",
  "Aprile",
  "Maggio",
  "Giugno",
  "Luglio",
  "Agosto",
  "Settembre",
  "Ottobre",
  "Novembre",
  "Dicembre",
];

const TurniConduttori = () => {
  const { user, activeCongregazione } = useAuth();
  const [occurrences, setOccurrences] = useState([]);
  const [serviceLeaders, setServiceLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoLoading, setAutoLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [lastAutocompilazione, setLastAutocompilazione] = useState(null);
  const today = new Date();
  const [period, setPeriod] = useState({
    month: today.getMonth() + 1,
    year: today.getFullYear(),
  });
  const [pendingAssignments, setPendingAssignments] = useState(new Map());
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState(null);

  const selectedMonth = period.month;
  const selectedYear = period.year;

  const hasPendingChanges = pendingAssignments.size > 0;

  const canManage =
    user?.ruolo === "admin" || user?.ruolo === "super_admin" || false;
  const activeCongregazioneId = activeCongregazione?.id;
  const isServizioSpeciale = Boolean(activeCongregazione?.servizio_speciale);
  const hasCongregazione = Boolean(activeCongregazioneId);

  const getMonthRange = (month, year) => {
    const startDate = new Date(Date.UTC(year, month - 1, 1));
    const endDate = new Date(Date.UTC(year, month, 0));
    return {
      startStr: startDate.toISOString().slice(0, 10),
      endStr: endDate.toISOString().slice(0, 10),
    };
  };

  const loadOccurrences = async () => {
    if (!hasCongregazione || !canManage || isServizioSpeciale) {
      setOccurrences([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { startStr, endStr } = getMonthRange(
        selectedMonth,
        selectedYear
      );
      const response = await api.get("/adunanze-servizio/turni", {
        params: {
          congregazione_id: activeCongregazioneId,
          data_inizio: startStr,
          data_fine: endStr,
        },
      });
      setOccurrences(
        Array.isArray(response.data?.occorrenze)
          ? response.data.occorrenze
          : []
      );
    } catch (error) {
      console.error("Errore caricamento adunanze:", error);
      toastError(
        error.response?.data?.message ||
          "Errore durante il caricamento delle adunanze"
      );
      setOccurrences([]);
    } finally {
      setLoading(false);
    }
  };

  const loadServiceLeaders = async () => {
    if (!hasCongregazione || !canManage || isServizioSpeciale) {
      setServiceLeaders([]);
      return;
    }
    try {
      const response = await api.get("/volontari", {
        params: {
          limit: 500,
          service_leader: true,
          sesso: "M",
          stato: "attivo",
        },
      });
      setServiceLeaders(response.data?.volontari || []);
    } catch (error) {
      console.error("Errore caricamento service leader:", error);
      toastError(
        error.response?.data?.message ||
          "Errore durante il caricamento dei conduttori"
      );
      setServiceLeaders([]);
    }
  };

  useEffect(() => {
    loadOccurrences();
    loadServiceLeaders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasCongregazione,
    canManage,
    isServizioSpeciale,
    activeCongregazioneId,
    selectedMonth,
    selectedYear,
  ]);

  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await fetch(plannerLogo);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => setLogoDataUrl(reader.result);
        reader.readAsDataURL(blob);
      } catch (error) {
        console.warn("Impossibile caricare il logo per l'export PDF:", error);
      }
    };
    loadLogo();
  }, []);

  const runAutocompilazione = async () => {
    const loadingToastId = toastLoading(
      "Autocompilazione conduttori A.S.\nSto elaborando le disponibilità del mese selezionato..."
    );
    try {
      setAutoLoading(true);
      const { startStr, endStr } = getMonthRange(
        selectedMonth,
        selectedYear
      );
      // Use preview: true to get a draft
      const response = await api.post("/adunanze-servizio/autocompilazione", {
        data_inizio: startStr,
        data_fine: endStr,
        preview: true,
      });
      
      const assigned = response.data?.assigned || [];
      const summary = response.data?.summary || {};
      
      // Update pending assignments with the proposal
      const newPending = new Map(pendingAssignments);
      let newCount = 0;
      
      assigned.forEach((a) => {
         if(a.status === 'assegnata' && a.leader?.id) {
           // Key: occurrence_id (we need to find the occurrence ID for this adunanza+date)
           // We have adunanza_id and data.
           const occ = occurrences.find(o => o.adunanza_id === a.adunanza_id && o.data === a.data);
           if(occ) {
             newPending.set(occ.occurrence_id, a.leader.id);
             newCount++;
           }
         }
      });
      
      setPendingAssignments(newPending);
      setLastAutocompilazione({
        timestamp: new Date().toISOString(),
        assignedCount: newCount,
        raw: response.data,
      });
      
      const skippedMsg = summary.skippedExisting > 0 ? ` (saltati ${summary.skippedExisting} già assegnati)` : "";

      if (newCount > 0) {
        toastSuccess(
          `Autocompilazione (anteprima): ${newCount} modifiche proposte${skippedMsg}. Clicca "Salva modifiche" per confermare.`
        );
      } else {
        toastSuccess("Nessuna nuova assegnazione proposta.");
      }

      if (summary?.unassigned > 0) {
         // ... existing warning logic ...
        const skippedMessage =
          summary.unassigned === 1
            ? "1 slot non assegnabile per mancanza di disponibilità"
            : `${summary.unassigned} slot non assegnabili per mancanza di disponibilità`;
        toastError(skippedMessage);
      }

      // Do NOT loadOccurrences yet, as we are in pending mode
    } catch (error) {
      console.error("Errore autocompilazione:", error);
      toastError(
        error.response?.data?.message ||
          "Errore durante l'autocompilazione delle adunanze"
      );
    } finally {
      setAutoLoading(false);
      toastDismiss(loadingToastId);
    }
  };

  const savePendingChanges = async () => {
    try {
      setLoading(true);
      const changes = [];
      
      pendingAssignments.forEach((leaderId, occurrenceId) => {
         const occ = occurrences.find(o => o.occurrence_id === occurrenceId);
         if(occ) {
           changes.push({
             adunanza_id: occ.adunanza_id,
             data: occ.data,
             service_leader_id: leaderId
           });
         }
      });
      
      if(changes.length === 0) return;

      await api.post("/adunanze-servizio/turni/batch", {
        assignments: changes
      });

      toastSuccess(`Salvate ${changes.length} modifiche.`);
      setPendingAssignments(new Map());
      loadOccurrences();
    } catch (error) {
      console.error("Errore salvataggio modifiche:", error);
      toastError("Errore durante il salvataggio delle modifiche.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotifications = async () => {
    if (hasPendingChanges) {
      toastInfo("Salva le modifiche prima di inviare le notifiche");
      return;
    }
    const { startStr, endStr } = getMonthRange(selectedMonth, selectedYear);
    setNotifyLoading(true);
    try {
      const { data: response } = await api.post(
        "/push/adunanze-servizio/send",
        {
          data_inizio: startStr,
          data_fine: endStr,
        }
      );
      toastSuccess(
        response?.message ||
          `Notifiche inviate (${response?.sent || 0} inviate)`
      );
    } catch (error) {
      console.error("Errore invio notifiche adunanze:", error);
      toastError(
        error.response?.data?.message ||
          "Errore durante l'invio delle notifiche"
      );
    } finally {
      setNotifyLoading(false);
    }
  };

  const cancelPendingChanges = () => {
    if(window.confirm("Annullare tutte le modifiche in sospeso?")) {
      setPendingAssignments(new Map());
      toastSuccess("Modifiche annullate.");
    }
  };

  const runResetTurni = async (adunanzaId = null) => {
    const { startStr, endStr } = getMonthRange(selectedMonth, selectedYear);
    const confirmed = window.confirm(
      adunanzaId
        ? `Cancellare le assegnazioni per questa adunanza dal ${startStr} al ${endStr}?`
        : `Cancellare le assegnazioni di tutte le adunanze dal ${startStr} al ${endStr}?`
    );
    if (!confirmed) return;
    const loadingToastId = toastLoading(
      adunanzaId
        ? "Reset in corso per l'adunanza selezionata..."
        : "Reset di tutte le assegnazioni conduttori del mese in corso..."
    );

    try {
      setResetLoading(true);
      const response = await api.post("/adunanze-servizio/reset-turni", {
        data_inizio: startStr,
        data_fine: endStr,
        adunanza_id: adunanzaId,
      });
      toastSuccess(
        response.data?.message ||
          "Assegnazioni reset nel periodo selezionato"
      );
      loadOccurrences();
    } catch (error) {
      console.error("Errore reset turni:", error);
      toastError(
        error.response?.data?.message ||
          "Errore durante il reset delle assegnazioni"
      );
    } finally {
      setResetLoading(false);
      toastDismiss(loadingToastId);
    }
  };

  const handleAssign = (occurrence, leaderId) => {
     const newMap = new Map(pendingAssignments);
     const val = leaderId ? Number(leaderId) : null;
     
     // Validazione opzionale: se il valore è uguale a quello salvato nel DB, rimuovi da pending?
     // Per ora semplice: se modifico, va in pending.
     // Se leaderId == null rimozione.
     
     newMap.set(occurrence.occurrence_id, val);
     setPendingAssignments(newMap);
  };

  const monthLabel = useMemo(() => {
    const monthName = monthNames[selectedMonth - 1] || "";
    return `${monthName} ${selectedYear}`;
  }, [selectedMonth, selectedYear]);

  const shiftMonth = (delta) => {
    setPeriod((prev) => {
      let nextMonth = prev.month + delta;
      let nextYear = prev.year;
      if (nextMonth > 12) {
        nextMonth = 1;
        nextYear += 1;
      } else if (nextMonth < 1) {
        nextMonth = 12;
        nextYear -= 1;
      }
      return { month: nextMonth, year: nextYear };
    });
  };

  const goToPreviousMonth = () => shiftMonth(-1);

  const goToNextMonth = () => shiftMonth(1);

  const formatDateLabel = (isoDate) => {
    if (!isoDate) return "";
    const dateObj = new Date(`${isoDate}T00:00:00Z`);
    return dateObj.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDayLabel = (weekdayNumber) => {
    if (
      typeof weekdayNumber !== "number" ||
      weekdayNumber < 1 ||
      weekdayNumber > 7
    ) {
      return "";
    }
    return giorniSettimana[weekdayNumber] || "";
  };

  const formatTimeLabel = (time) => {
    if (!time) return "—";
    return time.substring(0, 5);
  };

  const getEffectiveLeaderName = (occurrence) => {
    const mapHas = pendingAssignments.has(occurrence.occurrence_id);
    if (mapHas) {
      const pendingValue = pendingAssignments.get(occurrence.occurrence_id);
      if (!pendingValue) {
        return null;
      }
      const leaderFromList = serviceLeaders.find(
        (leader) => Number(leader.id) === Number(pendingValue)
      );
      if (leaderFromList) {
        return `${leaderFromList.nome} ${leaderFromList.cognome}`;
      }
      if (
        occurrence.leader &&
        Number(occurrence.leader.id) === Number(pendingValue)
      ) {
        return `${occurrence.leader.nome} ${occurrence.leader.cognome}`;
      }
      return null;
    }

    if (occurrence.leader) {
      return `${occurrence.leader.nome} ${occurrence.leader.cognome}`;
    }
    return null;
  };

  const groupedByAdunanza = useMemo(() => {
    const map = new Map();
    occurrences.forEach((occ) => {
      if (!map.has(occ.adunanza_id)) {
        map.set(occ.adunanza_id, {
          adunanza_id: occ.adunanza_id,
          titolo: occ.titolo,
          luogo: occ.luogo,
          indirizzo: occ.indirizzo,
          ora_inizio: occ.ora_inizio,
          giorno_settimana: occ.giorno_settimana,
          giorno_label:
            typeof occ.giorno_settimana === "number"
              ? giorniSettimana[occ.giorno_settimana]
              : "",
          occorrenze: [],
        });
      }
      map.get(occ.adunanza_id).occorrenze.push(occ);
    });

    return Array.from(map.values()).map((group) => ({
      ...group,
      occorrenze: group.occorrenze.sort((a, b) =>
        a.data.localeCompare(b.data)
      ),
    }));
  }, [occurrences]);

  const exportConduttoriPDF = async () => {
    if (!occurrences || occurrences.length === 0) {
      toastError("Non ci sono turni conduttori da esportare");
      return;
    }

    const unassigned = occurrences.filter(
      (occ) => !getEffectiveLeaderName(occ)
    );

    if (unassigned.length > 0) {
      const preview = unassigned
        .slice(0, 3)
        .map(
          (occ) =>
            `• ${formatDateLabel(occ.data)} ${formatTimeLabel(
              occ.ora_inizio
            )} - ${occ.luogo || "Luogo non indicato"}`
        )
        .join("\n");
      const confirmMessage = `Alcune adunanze non hanno ancora un conduttore assegnato:\n\n${preview}${
        unassigned.length > 3
          ? `\n... e altre ${unassigned.length - 3} adunanze`
          : ""
      }\n\nVuoi esportare comunque il PDF?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    setExporting(true);

    try {
      const JsPDF = await loadJsPdf();
      const doc = new JsPDF("portrait", "mm", "a4");
      registerMontserrat(doc);
      doc.setFont(FONT_MONTSERRAT, "normal");
      const pageWidth = doc.internal.pageSize.getWidth();

      if (logoDataUrl) {
        doc.addImage(logoDataUrl, "PNG", 12, 8, 18, 18);
      }

      doc.setFont(FONT_MONTSERRAT, "bold");
      doc.setFontSize(18);
      doc.text("Planner Turni - Conduttori A.S.", pageWidth / 2, 20, {
        align: "center",
      });

      doc.setFont(FONT_MONTSERRAT, "normal");
      doc.setFontSize(12);
      if (activeCongregazione?.nome) {
        doc.text(
          `Congregazione: ${activeCongregazione.nome}`,
          pageWidth / 2,
          28,
          { align: "center" }
        );
        doc.text(monthLabel, pageWidth / 2, 34, { align: "center" });
      } else {
        doc.text(monthLabel, pageWidth / 2, 28, { align: "center" });
      }

      const startY = activeCongregazione?.nome ? 40 : 36;

      const orderedOccurrences = [...occurrences].sort((a, b) => {
        const da = new Date(`${a.data}T${a.ora_inizio || "00:00"}:00`);
        const db = new Date(`${b.data}T${b.ora_inizio || "00:00"}:00`);
        return da - db;
      });

      const tableRows = orderedOccurrences.map((occ) => {
        const leaderName = getEffectiveLeaderName(occ) || "Non assegnato";

        return [
          occ.luogo || "—",
          formatDateLabel(occ.data),
          formatDayLabel(occ.giorno_settimana),
          formatTimeLabel(occ.ora_inizio),
          leaderName,
        ];
      });

      doc.autoTable({
        head: [
          [
            "Luogo",
            "Data",
            "Giorno",
            "Orario",
            "Conduttore",
          ],
        ],
        body: tableRows,
        startY,
        styles: {
          font: FONT_MONTSERRAT,
          fontSize: 9,
          cellPadding: 2,
        },
        headStyles: {
          font: FONT_MONTSERRAT,
          fillColor: [59, 130, 246],
          textColor: 255,
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: 36 },
          1: { cellWidth: 22 },
          2: { cellWidth: 22 },
          3: { cellWidth: 18 },
          4: { cellWidth: 40 },
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252],
        },
      });

      const finalY = doc.lastAutoTable.finalY || startY;
      doc.setFontSize(10);
      doc.setFont("helvetica", "italic");
      doc.text(
        `Esportato il ${new Date().toLocaleDateString(
          "it-IT"
        )} alle ${new Date().toLocaleTimeString("it-IT")}`,
        pageWidth / 2,
        finalY + 10,
        { align: "center" }
      );

      const filename = `turni_conduttori_${selectedYear}_${String(
        selectedMonth
      ).padStart(2, "0")}.pdf`;
      doc.save(filename);
      toastSuccess(`PDF esportato: ${filename}`);
    } catch (error) {
      console.error("Errore export conduttori:", error);
      toastError("Errore durante l'export dei turni conduttori");
    } finally {
      setExporting(false);
    }
  };

  if (!canManage) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white shadow rounded-lg p-6 text-center relative overflow-hidden">
          <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
          <p className="text-gray-600">
            Solo gli amministratori possono gestire i turni dei conduttori.
          </p>
        </div>
      </div>
    );
  }

  if (!hasCongregazione) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white shadow rounded-lg p-6 text-center relative overflow-hidden">
          <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
          <p className="text-gray-600">
            Seleziona prima una congregazione attiva per continuare.
          </p>
        </div>
      </div>
    );
  }

  if (isServizioSpeciale) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white shadow rounded-lg p-6 text-center space-y-2 relative overflow-hidden">
          <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
          <p className="text-sm font-semibold text-gray-900">
            Funzione non disponibile
          </p>
          <p className="text-gray-600 text-sm">
            Le congregazioni di servizio speciale utilizzano procedure dedicate
            per i turni dei conduttori. Tornare alla sezione principale per
            registrare disponibilità e assegnazioni.
          </p>
        </div>
      </div>
    );
  }

  const renderOccurrenceCell = (occurrence) => {
    const dateObj = new Date(`${occurrence.data}T00:00:00Z`);
    const formattedDate = dateObj.toLocaleDateString("it-IT", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
    });
    const oraLabel = occurrence.ora_inizio
      ? occurrence.ora_inizio.substring(0, 5)
      : "—";
    // Calcolo valori effettivi considerando le modifiche pendenti
    const isPending = pendingAssignments.has(occurrence.occurrence_id);
    const effectiveLeaderId = isPending 
       ? pendingAssignments.get(occurrence.occurrence_id) 
       : occurrence.leader?.id;
    
    // Serve l'oggetto leader completo per il rendering del nome e per le stats
    // Ma per le stats, dobbiamo usare effectiveLeaderId
    
    const assigned = Boolean(effectiveLeaderId);
    const selectValue = effectiveLeaderId || "";

    // Helper stats dinamico
    const getStats = (leaderId) => {
      let assignmentsCount = 0;
      let availabilityCount = 0;

      occurrences.forEach((occ) => {
        // Determina chi è assegnato a questa occorrenza (pending > db)
        const occIsPending = pendingAssignments.has(occ.occurrence_id);
        const occLeaderId = occIsPending 
           ? pendingAssignments.get(occ.occurrence_id) 
           : occ.leader?.id;

        // Conta assegnazioni
        if (occLeaderId === leaderId) {
          assignmentsCount++;
        }
        // Conta disponibilità (fissa dal DB)
        if (
          occ.disponibili &&
          occ.disponibili.some((d) => d.id === leaderId)
        ) {
          availabilityCount++;
        }
      });

      return { assignmentsCount, availabilityCount };
    };

    const availableLeaderIds = new Set(
      (occurrence.disponibili || []).map((leader) => leader.id)
    );
    const availableOptions = serviceLeaders.filter((leader) =>
      availableLeaderIds.has(leader.id)
    );
    const manualOptions = serviceLeaders.filter(
      (leader) => !availableLeaderIds.has(leader.id)
    );

    return (
      <div
        key={occurrence.occurrence_id}
        className={`w-full rounded-lg border p-3 flex flex-col gap-2 ${
          isPending
            ? "border-amber-400 bg-amber-50"
            : "border-blue-100 bg-blue-50"
        } sm:min-w-[240px]`}
      >
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span className="font-semibold text-blue-700">{formattedDate}</span>
          <span className="flex items-center gap-1">
            <ClockIcon className="h-4 w-4 text-gray-400" />
            {oraLabel}
          </span>
        </div>
        <div className="text-sm font-semibold text-blue-700">
          {assigned ? "1/1 assegnato" : "0/1 assegnati"}
        </div>
        <div className="flex items-center gap-2">
          <select
            className="w-full rounded-md border-gray-300 text-sm"
            value={selectValue}
            onChange={(e) =>
              handleAssign(
                occurrence,
                e.target.value ? e.target.value : null
              )
            }
          >
            <option value="">Seleziona conduttore...</option>
            {availableOptions.map((leader) => {
              const stats = getStats(leader.id);
              return (
                <option key={`available-${leader.id}`} value={leader.id}>
                  {leader.nome} {leader.cognome} • [
                  {stats.assignmentsCount} - {stats.availabilityCount}]
                </option>
              );
            })}
            {manualOptions.length > 0 && availableOptions.length > 0 && (
              <option disabled>──────────</option>
            )}
            {manualOptions.map((leader) => {
              const stats = getStats(leader.id);
              return (
                <option key={`manual-${leader.id}`} value={leader.id}>
                  {leader.nome} {leader.cognome} • [
                  {stats.assignmentsCount} - {stats.availabilityCount}] (non disponibile)
                </option>
              );
            })}
          </select>
          {isPending && assigned && (
            <button
              onClick={() => {
                const newMap = new Map(pendingAssignments);
                newMap.delete(occurrence.occurrence_id);
                setPendingAssignments(newMap);
                toastSuccess("Conduttore in sospeso rimosso");
              }}
              className="flex-shrink-0 text-red-600 hover:text-red-800 hidden lg:flex"
              title="Annulla assegnazione in sospeso"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          )}
        </div>
        <div
          className={`text-xs ${
            isPending ? "text-amber-600 font-medium" : "text-gray-500"
          }`}
        >
          {isPending
            ? "Assegnazione in sospeso (non salvata)"
            : assigned
            ? "Assegnazione salvata"
            : "Non assegnato"}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
      <div className="bg-white shadow rounded-lg p-4 md:p-6 relative overflow-hidden">
        <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <ConduttoreIcon />
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Turni Conduttori A.S.
              </h1>
              <p className="text-sm text-gray-600">
                Autocompilazione e gestione manuale dei conduttori per le
                adunanze del mese.
              </p>
              <div className="accent-line mt-3" />
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-sm text-gray-600 space-y-1">
              {lastAutocompilazione && (
                <p className="text-xs text-gray-500">
                  Ultima autocompilazione:{" "}
                  {new Date(
                    lastAutocompilazione.timestamp
                  ).toLocaleString("it-IT")}
                  , assegnate: {lastAutocompilazione.assignedCount || 0}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 text-center sm:text-left md:flex md:flex-wrap">
              {hasPendingChanges && (
                <>
                  <button
                    onClick={savePendingChanges}
                    className="inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 animate-pulse w-full md:w-auto"
                  >
                    <CheckIcon className="h-5 w-5 mr-2" />
                    Salva modifiche
                  </button>
                  <button
                    onClick={cancelPendingChanges}
                    className="inline-flex items-center justify-center px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 w-full md:w-auto"
                  >
                    <XMarkIcon className="h-5 w-5 mr-2" />
                    Annulla
                  </button>
                </>
              )}
              <button
                onClick={handleSendNotifications}
                disabled={notifyLoading || loading || hasPendingChanges}
                className="inline-flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 w-full md:w-auto"
              >
                {notifyLoading ? (
                  <>
                    <span className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Invio notifiche...
                  </>
                ) : (
                  <>
                    <BellIcon className="h-5 w-5 mr-2" />
                    Invia notifiche
                  </>
                )}
              </button>
              <button
                onClick={runAutocompilazione}
                disabled={autoLoading}
                className="inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 w-full md:w-auto"
              >
                <BoltIcon className="h-5 w-5 mr-2" />
                {autoLoading ? "Elaborazione..." : "Autocompila"}
              </button>
              <button
                onClick={exportConduttoriPDF}
                disabled={exporting || loading}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 w-full md:w-auto"
              >
                <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                {exporting ? "Esportazione..." : "Export PDF"}
              </button>
              <button
                onClick={() => runResetTurni()}
                disabled={resetLoading}
                className="btn-danger inline-flex items-center px-4 py-2 justify-center whitespace-nowrap disabled:opacity-50 w-full md:w-auto"
              >
                <TrashIcon className="h-5 w-5 mr-2" />
                {resetLoading ? "Reset..." : "Reset mese"}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-center sm:justify-start gap-3 text-sm text-gray-700">
            <div className="flex items-center gap-2">
              <button
                onClick={goToPreviousMonth}
                className="p-2 rounded-full border border-gray-200 hover:bg-gray-100 text-gray-600"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <span className="text-sm font-medium text-gray-900">
                {monthLabel}
              </span>
              <button
                onClick={goToNextMonth}
                className="p-2 rounded-full border border-gray-200 hover:bg-gray-100 text-gray-600"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white shadow rounded-lg p-6 text-center text-gray-600 relative overflow-hidden">
          <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
          Caricamento in corso...
        </div>
      ) : occurrences.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-6 text-center text-gray-600 relative overflow-hidden">
          <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
          Non sono presenti adunanze programmate. Crea nuove adunanze dalla
          sezione “Adunanze Servizio”.
        </div>
      ) : (
        groupedByAdunanza.map((group) => {
          const oraLabel = group.ora_inizio
            ? group.ora_inizio.substring(0, 5)
            : "";
          const dayLabel =
            typeof group.giorno_settimana === "number"
              ? giorniSettimana[group.giorno_settimana]
              : "";
          const headerParts = [];
          if (dayLabel) {
            headerParts.push(dayLabel);
          }
          if (oraLabel) {
            headerParts.push(`ore ${oraLabel}`);
          }
          headerParts.push(group.luogo);
          const headerText = headerParts.join(" · ");
          return (
            <div
              key={group.adunanza_id}
              className="bg-white shadow rounded-lg p-4 md:p-5 space-y-4 relative overflow-hidden"
            >
              <span className="accent-bar top-4 bottom-4" aria-hidden="true" />
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="space-y-1">
                  <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <MapPinIcon className="h-5 w-5 text-primary-600" />
                    <span>{headerText}</span>
                  </div>
                  {group.indirizzo && (
                    <div className="text-sm text-gray-500 ml-7">
                      {group.indirizzo}
                    </div>
                  )}
                  {group.titolo && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 ml-7">
                      <ClockIcon className="h-4 w-4 text-gray-400" />
                      <span>{group.titolo}</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() =>
                      runResetTurni(group.adunanza_id)
                    }
                    disabled={resetLoading}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ArrowPathIcon className="h-4 w-4 mr-1" />
                    Reset adunanza
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  {group.occorrenze.map((occ) => renderOccurrenceCell(occ))}
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default TurniConduttori;
const giorniSettimana = [
  "",
  "Lunedì",
  "Martedì",
  "Mercoledì",
  "Giovedì",
  "Venerdì",
  "Sabato",
  "Domenica",
];
